import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaChevronUp, FaTelegramPlane } from "react-icons/fa";
import { FaChevronDown } from "react-icons/fa";
import projectsData from "../data/projects.json";

const GIF_SWAP_MS = 5000;

function ProjectGif({ imagesrc, imagesrc2, alt }) {
  const sources = useMemo(() => {
    const list = [`./img/${imagesrc}.gif`];
    if (imagesrc2) list.push(`./img/${imagesrc2}.gif`);
    return list;
  }, [imagesrc, imagesrc2]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (sources.length < 2) return undefined;
    const id = setInterval(() => {
      setIndex((current) => (current + 1) % sources.length);
    }, GIF_SWAP_MS);
    return () => clearInterval(id);
  }, [sources.length]);

  return (
    <img
      src={sources[index]}
      alt={alt}
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
}

export default function Portfolio(props) {
  function touchStarted(event) {
    const startY = event.touches[0].clientY;
    const portfolio = document.getElementById("portfolio");
    portfolio.addEventListener("touchend", touchEnd);
    function touchEnd(event) {
      const endY = event.changedTouches[0].clientY;
      if (endY > startY + 200) {
        // props.swipeUp()
        return;
      } else if (endY < startY - 200) {
        // props.swipeDown()
        return;
      }
    }
  }

  const projectsAllX = projectsData;

  const projectsAll = useMemo(
    () => [...projectsAllX].reverse(),
    [projectsAllX],
  );

  // Helper function to update URL with filter
  function updateUrlFilter(filter) {
    const validFilters = [
      "all",
      "web3",
      "typescript",
      "python",
      "uiux",
      "rust",
    ];
    if (validFilters.includes(filter)) {
      const url = new URL(window.location);
      if (filter === "all") {
        url.searchParams.delete("filter");
      } else {
        url.searchParams.set("filter", filter);
      }
      window.history.pushState({}, "", url);
    }
  }

  // Helper function to get filter from URL
  function getFilterFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get("filter");
    const validFilters = [
      "all",
      "web3",
      "typescript",
      "python",
      "uiux",
      "rust",
    ];
    return validFilters.includes(filter) ? filter : "all";
  }

  const [projectVisible, setProjectVisible] = React.useState(projectsAll);

  const [activeFilter, setActiveFilter] = React.useState("all");

  const [projectAnim, setProjectAnim] = React.useState(false);

  const scrollStep = 250;
  const scrollIntervalRef = useRef(null);

  const scrollUp = useCallback(() => {
    const projectsContainer = document.getElementById("projects");
    if (!projectsContainer) return;

    const startPosition = projectsContainer.scrollTop;
    const targetPosition = Math.max(0, startPosition - scrollStep);
    const distance = targetPosition - startPosition;
    const duration = 300; // milliseconds
    let startTime = null;

    const animateScroll = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing function for smooth deceleration
      const ease = 1 - Math.pow(1 - progress, 3);

      projectsContainer.scrollTop = startPosition + distance * ease;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }, []);

  const scrollDown = useCallback(() => {
    const projectsContainer = document.getElementById("projects");
    if (!projectsContainer) return;

    const startPosition = projectsContainer.scrollTop;
    const maxScroll =
      projectsContainer.scrollHeight - projectsContainer.clientHeight;
    const targetPosition = Math.min(maxScroll, startPosition + scrollStep);
    const distance = targetPosition - startPosition;
    const duration = 300; // milliseconds
    let startTime = null;

    const animateScroll = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing function for smooth deceleration
      const ease = 1 - Math.pow(1 - progress, 3);

      projectsContainer.scrollTop = startPosition + distance * ease;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }, []);

  // Continuous scrolling handlers
  const startContinuousScroll = useCallback(
    (direction) => {
      // Clear any existing interval
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }

      // Start scrolling immediately
      if (direction === "up") {
        scrollUp();
      } else {
        scrollDown();
      }

      // Continue scrolling at regular intervals
      scrollIntervalRef.current = setInterval(() => {
        if (direction === "up") {
          scrollUp();
        } else {
          scrollDown();
        }
      }, 150); // Scroll every 150ms while held down
    },
    [scrollUp, scrollDown],
  );

  const stopContinuousScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  // Helper function to apply filter without updating URL
  const applyFilter = useCallback(
    function (filter, updateUrl = true) {
      if (updateUrl) {
        updateUrlFilter(filter);
      }

      setProjectAnim(true);
      setActiveFilter(filter);

      setTimeout(function () {
        let newState;
        if (filter === "all") {
          newState = projectsAll;
        } else if (filter === "uiux") {
          newState = projectsAll.filter((project) => {
            return project.type.includes("uiux");
          });
        } else if (filter === "typescript") {
          newState = projectsAll.filter((project) => {
            return project.techused.some(
              (tech) => tech.name.toLowerCase() === "typescript",
            );
          });
        } else if (filter === "web3") {
          newState = projectsAll.filter((project) => {
            return project.type.includes("web3");
          });
        } else if (filter === "rust") {
          newState = projectsAll.filter((project) => {
            return project.type.includes("rust");
          });
        } else if (filter === "python") {
          newState = projectsAll.filter((project) => {
            return project.type.includes("python");
          });
        } else {
          newState = projectsAll;
        }

        setProjectVisible(newState);
        setProjectAnim(false);
      }, 500);
    },
    [projectsAll],
  );

  function showUIUX() {
    applyFilter("uiux");
  }

  // function showHtmlCss(){
  //     setProjectAnim(true)

  //     setTimeout(
  //         function(){
  //             setProjectVisible(()=>{
  //                 const newState = projectsAll.filter((project)=>{
  //                     return project.type === "htmlcss"
  //                 })
  //                 return newState
  //             })
  //             setProjectAnim(false)
  //         }, 500
  //     )
  //     setActiveFilter("htmlcss")

  // }

  // function showNode(){
  //     setProjectAnim(true)

  //     setTimeout(
  //         function(){
  //             setProjectVisible(()=>{
  //                 const newState = projectsAll.filter((project)=>{
  //                     return project.type === "node"
  //                 })
  //                 return newState
  //             })
  //             setProjectAnim(false)
  //         }, 500
  //     )
  //     setActiveFilter("node")
  // }

  function showTypeScript() {
    applyFilter("typescript");
  }

  function showWeb3() {
    applyFilter("web3");
  }

  function showRust() {
    applyFilter("rust");
  }

  function showPython() {
    applyFilter("python");
  }

  function showAll() {
    applyFilter("all");
  }

  React.useEffect(
    function () {
      const projectHolder = document.getElementById("projects");

      projectHolder.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    [projectAnim],
  );

  // Initialize filter from URL on mount
  React.useEffect(
    function () {
      const filterFromUrl = getFilterFromUrl();
      // Only apply filter if it's not "all" (default state already shows all)
      if (filterFromUrl !== "all") {
        setActiveFilter(filterFromUrl);
        // Apply filter directly without animation on initial load
        let newState;
        if (filterFromUrl === "uiux") {
          newState = projectsAll.filter((project) => {
            return project.type.includes("uiux");
          });
        } else if (filterFromUrl === "typescript") {
          newState = projectsAll.filter((project) => {
            return project.techused.some(
              (tech) => tech.name.toLowerCase() === "typescript",
            );
          });
        } else if (filterFromUrl === "web3") {
          newState = projectsAll.filter((project) => {
            return project.type.includes("web3");
          });
        } else if (filterFromUrl === "rust") {
          newState = projectsAll.filter((project) => {
            return project.type.includes("rust");
          });
        } else if (filterFromUrl === "python") {
          newState = projectsAll.filter((project) => {
            return project.type.includes("python");
          });
        } else {
          newState = projectsAll;
        }
        setProjectVisible(newState);
      }
    },
    [projectsAll], // Run when projectsAll is ready
  );

  // Handle browser back/forward navigation
  React.useEffect(
    function () {
      function handlePopState() {
        const filterFromUrl = getFilterFromUrl();
        applyFilter(filterFromUrl, false); // Don't update URL on popstate
      }

      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    },
    [applyFilter],
  );

  // Sync URL with active filter when on portfolio section
  React.useEffect(
    function () {
      function syncFilterToUrl() {
        // Check if we're on the portfolio route
        const pathname = window.location.pathname;
        if (pathname === "/portfolio" || pathname === "/portfolio/") {
          const filterFromUrl = getFilterFromUrl();
          // If the active filter doesn't match the URL, update the URL
          if (activeFilter !== filterFromUrl) {
            updateUrlFilter(activeFilter);
          }
        }
      }

      // Sync immediately
      syncFilterToUrl();

      // Also sync when pathname might change (via popstate or navigation)
      function handlePathChange() {
        syncFilterToUrl();
      }

      // Listen for popstate (back/forward)
      window.addEventListener("popstate", handlePathChange);

      // Check periodically in case navigation happens without popstate
      const intervalId = setInterval(syncFilterToUrl, 500);

      return () => {
        window.removeEventListener("popstate", handlePathChange);
        clearInterval(intervalId);
      };
    },
    [activeFilter], // Run when activeFilter changes
  );

  const projectsReversed = projectVisible;

  const projectEl = projectsReversed.map((project) => {
    const projectTech = project.techused.map((tech) => {
      return (
        <div className="tech" key={tech.name}>
          {tech.name}
        </div>
      );
    });

    // Check if the livesite is a Google Drive, GitHub, or Figma link
    const isGoogleDrive = project.livesite?.includes("drive.google.com");
    const isGitHub = project.livesite?.includes("github.com");
    const isFigma = project.livesite?.includes("figma.com");
    const shouldOpenModal = !isGoogleDrive && !isGitHub && !isFigma;
    const isBot = Boolean(project["is-bot"]);

    return (
      <div
        className="project"
        key={project.name + Math.ceil(Math.random() * 100)}
        onClick={() => {
          if (shouldOpenModal && props.openProjectModal) {
            props.openProjectModal(project);
          }
        }}
        style={{ cursor: shouldOpenModal ? "pointer" : "default" }}
      >
        <div
          className={`project--bg ${project?.["center-image"] ? "center" : "default"}`}
          style={project?.["is-gif"] ? {} : {
            backgroundImage: `url(./img/${project.imagesrc}.png)`,
          }}
        >
          {project?.["is-gif"] && (
            <ProjectGif
              imagesrc={project.imagesrc}
              imagesrc2={project.imagesrc2}
              alt={project.name}
            />
          )}
        </div>
        <div className="site-info">
          <div className="live-links" onClick={(e) => e.stopPropagation()}>
            <a
              href={project.livesite}
              target="_blank"
              rel="noreferrer"
              className={isBot ? "bot-link" : ""}
            >
              {isBot ? (
                "Open bot"
              ) : (
                <i className="fa fa-arrow-up-right-from-square" />
              )}
              {isBot && (
                <FaTelegramPlane style={{ marginLeft: 5, fontSize: 18 }} />
              )}
            </a>
            <a
              href={project.githublink}
              target="_blank"
              rel="noreferrer"
              className={
                project.githublink === "https://github.com/bytegen-dev"
                  ? "private-repo"
                  : ""
              }
            >
              <i className="fa-brands fa-github" />
            </a>
          </div>
          <div className="description">{project.description}</div>

          <div>{projectTech}</div>
        </div>

        <div className="absolute">
          <span>{project.name}</span>
        </div>
        <div className="typex">{project.typex}</div>
      </div>
    );
  });

  return (
    <div id="portfolio" className={props.className} onTouchStart={touchStarted}>
      <div className="heading">
        <h1>{props.isFrench ? "Mes Projets" : "My Projects"}</h1>
        <div className="filter-bar">
          <div
            onClick={showAll}
            className={activeFilter === "all" ? "filter active" : "filter"}
          >
            {props.isFrench ? "TOUS" : "ALL"}
          </div>

          <div
            onClick={showWeb3}
            className={activeFilter === "web3" ? "filter active" : "filter"}
          >
            WEB3
          </div>
          <div
            onClick={showTypeScript}
            className={
              activeFilter === "typescript" ? "filter active" : "filter"
            }
          >
            TYPESCRIPT
          </div>
          <div
            onClick={showPython}
            className={activeFilter === "python" ? "filter active" : "filter"}
          >
            PYTHON
          </div>
          <div
            onClick={showUIUX}
            className={activeFilter === "uiux" ? "filter active" : "filter"}
          >
            UI/UX
          </div>
          <div
            onClick={showRust}
            className={activeFilter === "rust" ? "filter active" : "filter"}
          >
            RUST
          </div>
          {/* <div onClick={showNode} className={activeFilter === "node"?"filter active":"filter"}>NODE JS</div> */}
        </div>
      </div>
      <div id="projects" className={projectAnim ? "projects hide" : "projects"}>
        {projectEl}

        {projectEl.length < 1 && (
          <div className="empty">
            <i className="fa fa-heart-crack fa-beat-fade" />
          </div>
        )}
      </div>
      <div className="projects-scroll">
        <button
          className="scroll-button"
          onClick={scrollUp}
          onMouseDown={() => startContinuousScroll("up")}
          onMouseUp={stopContinuousScroll}
          onMouseLeave={stopContinuousScroll}
          onTouchStart={() => startContinuousScroll("up")}
          onTouchEnd={stopContinuousScroll}
        >
          <FaChevronUp />
        </button>
        <button
          className="scroll-button"
          onClick={scrollDown}
          onMouseDown={() => startContinuousScroll("down")}
          onMouseUp={stopContinuousScroll}
          onMouseLeave={stopContinuousScroll}
          onTouchStart={() => startContinuousScroll("down")}
          onTouchEnd={stopContinuousScroll}
        >
          <FaChevronDown />
        </button>
      </div>
    </div>
  );
}
